import { useId, useImperativeHandle, useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { CurecallButton, ECurecallButtonAppearance, ECurecallSize } from '@honestica/curecall-ui'
import { TMedEventForm } from 'src/components/followup/generic-followup-form.service'
import { getDefaultValues } from './med-event-form.service'
import { useFollowupFormContext } from 'src/components/followup/generic-followup-form.component'
import { TSelectedMedEvent, TCompletedMedEvent } from '../generic-followup-form.type'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTranslation } from 'react-i18next'
import MedEventFields from 'src/components/med-events/med-event-fields.component'
import { TPatient } from 'src/types/patient'
import { useCurrentProject } from 'src/providers/current-project.provider'

interface Props {
  onCloseCallback?: VoidFunction
  completedMedEvent?: TCompletedMedEvent
  isEditing?: boolean
}

const FollowupMedEventForm = ({ onCloseCallback, completedMedEvent, isEditing = false }: Props) => {
  const { getMedEventLabelById } = useCurrentProject()
  const { t } = useTranslation()
  const { activeDoctors } = useCurrentProject()
  const {
    selectedMedEvent: medEventToShow,
    medEventFormRef,
    patient,
    completedMedEvents,
    cancelMedEventToComplete,
    addOrUpdateCompletedMedEvent,
  } = useFollowupFormContext()

  const methods = useForm<TMedEventForm>({
    defaultValues: getDefaultValues(medEventToShow, completedMedEvent, isEditing),
  })
  const {
    formState: { errors },
  } = methods

  const id = useId()

  const getFormValues = useCallback(async (): Promise<TCompletedMedEvent | null> => {
    const isValid = await methods.trigger()
    if (!isValid) {
      return null
    }
    const values = methods.getValues()
    const medEventUI = medEventToShow as TSelectedMedEvent
    delete medEventUI?.errorMessage
    const isUpdated = !!completedMedEvent
    const newCompletedMedEvent: TCompletedMedEvent = {
      LocationId: values.LocationId && +values.LocationId,
      UserId: values.UserId && +values.UserId,
      date: values.date,
      medEventUI,
      uniqId: isUpdated ? completedMedEvent.uniqId : id,
      patientMedEventId: isUpdated ? completedMedEvent.patientMedEventId : undefined,
      convocationDate: values.convocationDate,
      convocationDateSetBy: values.convocationDateSetBy || undefined,
    }
    return newCompletedMedEvent
  }, [methods, medEventToShow, completedMedEvent, id])

  const onSubmit = useCallback(async () => {
    const newCompletedMedEvent = await getFormValues()
    if (newCompletedMedEvent) {
      addOrUpdateCompletedMedEvent?.(newCompletedMedEvent)
      onCloseCallback?.()
    }
  }, [getFormValues, addOrUpdateCompletedMedEvent, onCloseCallback])

  // this is to expose the getFormValues function to the parent component
  // it allows to validate opened form on clic on "Terminer"
  useImperativeHandle(medEventFormRef, () => ({ getFormValues }), [getFormValues])

  const getErrorPadding = () => {
    const formErrorMessages = Object.values(errors)
    if (formErrorMessages.length) {
      const hasAnErrorALongMessage = formErrorMessages.some(({ message }) => message && message?.length > 80)

      return hasAnErrorALongMessage ? 'c-mt-20' : 'c-mt-12'
    }
    const shouldAddSpaceForDoctorWarningMessage =
      activeDoctors.length === 0 && medEventToShow?.properties.some((prop) => prop.fieldName === 'UserId')
    return shouldAddSpaceForDoctorWarningMessage ? 'c-mt-14' : ''
  }

  if (!medEventToShow) return null

  const shouldShowCancelButton = () => {
    const isMedEventAlreadyBeenFilled =
      completedMedEvent?.medEventUI?.MedEvent.id === medEventToShow.MedEvent.id
    const isMedEventRequired = medEventToShow.validationType === 'REQUIRED'
    return isEditing || isMedEventAlreadyBeenFilled || !isMedEventRequired
  }

  return (
    <FormProvider {...methods}>
      <form method="POST" action="#" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="c-px-4 c-py-6 c-bg-grey-50 c-rounded-md c-w-full">
          <h2 className="c-text-xl c-text-grey-700 c-font-bold c-mb-5">
            {getMedEventLabelById(medEventToShow.MedEvent.id)}
            {}
            {medEventToShow.validationType === 'REQUIRED' && <span style={{ color: 'red' }}> *</span>}
          </h2>
          {medEventToShow.errorMessage && (
            <p className="c-font-nexa c-text-sm c-text-danger-500 xl:c-text-base xl:c-tracking-tight 2xl:c-text-lg 2xl:c-tracking-tightest">
              {medEventToShow.errorMessage}
            </p>
          )}
          <MedEventFields
            selectedMedEvent={medEventToShow}
            patient={patient as TPatient}
            defaultLocationId={completedMedEvents?.length ? completedMedEvents[0]?.LocationId : undefined}
            isEditing={isEditing}
            errorClassName={getErrorPadding()}
          />
          <div className={twMerge('c-flex c-justify-end c-gap-8 c-items-end c-mt-4', getErrorPadding())}>
            {shouldShowCancelButton() && (
              <CurecallButton
                onClick={() => {
                  cancelMedEventToComplete?.()
                  onCloseCallback?.()
                }}
                size={ECurecallSize.SMALL}
                appearance={ECurecallButtonAppearance.CANCEL}
              >
                {t('common.actions.cancel')}
              </CurecallButton>
            )}
            <CurecallButton
              type="submit"
              size={ECurecallSize.SMALL}
              appearance={ECurecallButtonAppearance.SECONDARY}
            >
              {completedMedEvent
                ? t('components.generic-followups-form.validate-changes')
                : t('components.generic-followups-form.validate')}
            </CurecallButton>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default FollowupMedEventForm
